/**
 * Mobile-only summary bar at the bottom of the dashboard.
 *
 * On a 375 px phone the desktop layout (260 px right sidebar + 360 px
 * event-feed column) leaves only ~63 px for the Pixi canvas -- the
 * building exterior with the pod windows is the whole reason the
 * dashboard exists, so squeezing it that hard is a non-starter.
 *
 * The bar shows a 2-row grid of compact summary chips for the seven
 * CenterBook sections plus the Event Feed.  Each chip displays the
 * one number that matters for that section (aggregate P&L, alert
 * count, max correlation, ...).  Tap on a chip pops the corresponding
 * section open in a BottomSheet, where the user gets the full widget
 * -- legend, bars, matrix, top pairs, whatever.
 *
 * Why we still compute everything here instead of forwarding the
 * tap to ``<CenterBookPanel>``:
 *
 *   * The selectors are cheap; running them in both components is
 *     free because the store gives identical references when the
 *     underlying data doesn't move (the panel itself does the same).
 *   * Each chip shows a *different* slice (just the headline number,
 *     not the full body), so rendering the panel and hiding most of
 *     it would just be wasted DOM.
 *   * The full-section render still uses the panel's exported
 *     section components (CapitalAllocationSection, PnlSection, ...)
 *     so chip and sheet stay in lockstep.
 */
import { useMemo, useState } from 'react';

import { BottomSheet } from '@/components/BottomSheet';
import {
  AlertsSection,
  CapitalAllocationSection,
  CapitalFlowSection,
  ConcentrationSection,
  CorrelationMatrixSection,
  PnlSection,
  RiskBreakdownSection,
} from '@/components/CenterBookPanel';
import { EventFeed } from '@/components/EventFeed';
import { Tooltip } from '@/components/Tooltip';
import { formatMillions } from '@/state/centerbookFormat';
import { useDashboardStore } from '@/state/store';
import {
  selectAlertsRollup,
  selectCapitalFlowEvents,
  selectCapitalMap,
  selectCorrelationMatrix,
  selectPnlDistribution,
  selectRiskBreakdown,
  selectTickerConcentration,
  type RiskState,
} from '@/state/centerbookSelectors';

type SheetKey =
  | 'capital'
  | 'pnl'
  | 'alerts'
  | 'risk'
  | 'concentration'
  | 'correlation'
  | 'capital-flow'
  | 'event-feed';

const RISK_DOT_COLOR: Record<RiskState, string> = {
  active: '#2ea043',
  watch: '#d29922',
  reduced: '#db6d28',
  frozen: '#58a6ff',
};

function signedMillions(rub: number): string {
  const sign = rub >= 0 ? '+' : '−';
  return `${sign}₽${formatMillions(Math.abs(rub))}`;
}

export function MobileBottomBar() {
  const floors = useDashboardStore((s) => s.building?.floors);
  const pods = useDashboardStore((s) => s.pods);
  const feed = useDashboardStore((s) => s.feed);

  const summaries = useMemo(
    () => (floors ? floors.flatMap((f) => f.pods) : []),
    [floors],
  );
  const podNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of summaries) m[p.pod_id] = p.name;
    return m;
  }, [summaries]);

  const capitalMap = useMemo(() => selectCapitalMap(summaries), [summaries]);
  const pnl = useMemo(() => selectPnlDistribution(summaries), [summaries]);
  const alerts = useMemo(
    () => selectAlertsRollup(pods, podNames),
    [pods, podNames],
  );
  const riskBreakdown = useMemo(() => selectRiskBreakdown(summaries), [summaries]);
  const concentration = useMemo(
    () => selectTickerConcentration(pods, podNames),
    [pods, podNames],
  );
  const correlation = useMemo(
    () =>
      selectCorrelationMatrix(
        summaries.map((p) => p.pod_id),
        pods,
      ),
    [summaries, pods],
  );
  const capitalFlow = useMemo(
    () => selectCapitalFlowEvents(feed, podNames),
    [feed, podNames],
  );

  const [open, setOpen] = useState<SheetKey | null>(null);
  const close = () => setOpen(null);

  // Don't render until we have a building snapshot -- the chips would
  // all read zero and confuse the user that something's broken.
  if (summaries.length === 0) return null;

  const pnlSign =
    pnl.aggregateNet > 0 ? '#3fb950' : pnl.aggregateNet < 0 ? '#f85149' : '#8b949e';
  const alertsAccent = alerts.totalErrors > 0 ? '#f85149' : '#3fb950';
  const corrAccent =
    correlation.maxCorrelation != null && correlation.maxCorrelation >= 0.7
      ? '#f85149'
      : correlation.maxCorrelation != null && correlation.maxCorrelation >= 0.4
        ? '#d29922'
        : '#8b949e';
  const tiers: RiskState[] = ['active', 'watch', 'reduced', 'frozen'];
  const lastEvent = feed.length > 0 ? feed[feed.length - 1] : null;

  return (
    <>
      <nav
        aria-label="Дашборд — быстрые виджеты"
        style={{
          background: '#0d1117',
          borderTop: '1px solid #21262d',
          padding: '8px 10px 10px',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gridAutoRows: 'auto',
          gap: 6,
          // Honour iOS home-bar safe area so taps near the bottom
          // edge don't dispatch to the system gesture instead.
          paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
          // Force a positive stacking context.  Without ``position:
          // relative`` (and an explicit z-index) the WebGL canvas
          // from BuildingPixiView could end up on the same layer and
          // overpaint the bar on iOS Safari -- the bar then looked
          // missing on real devices even though the DOM was correct.
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Chip
          label="P&L"
          tooltip={`Суммарный P&L по фонду за день: ${signedMillions(pnl.aggregateNet)}. Тап — разбивка по подам.`}
          value={signedMillions(pnl.aggregateNet)}
          accent={pnlSign}
          onClick={() => setOpen('pnl')}
        />
        <Chip
          label="Alerts"
          tooltip={`Ошибок за сессию: ${alerts.totalErrors}. Тап — список подов и последняя ошибка по каждому.`}
          value={String(alerts.totalErrors)}
          accent={alertsAccent}
          onClick={() => setOpen('alerts')}
        />
        <Chip
          label="Risk"
          tooltip={`Поды по тирам риска. active·watch·reduced·frozen. Тап — стэкбар + список.`}
          value={
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
              {tiers.map((t) => {
                const n = riskBreakdown.buckets[t].count;
                if (n === 0) return null;
                return (
                  <span
                    key={t}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 2,
                      color: '#e6edf3',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: RISK_DOT_COLOR[t],
                      }}
                    />
                    {n}
                  </span>
                );
              })}
            </span>
          }
          onClick={() => setOpen('risk')}
        />
        <Chip
          label="Corr"
          tooltip={
            correlation.maxCorrelation != null
              ? `Максимальная парная корреляция: ${correlation.maxCorrelation.toFixed(2)}. Тап — матрица N×N + топ-3 пары.`
              : 'Нет данных для парных корреляций. Нужно минимум 2 пода с позициями.'
          }
          value={
            correlation.maxCorrelation != null
              ? correlation.maxCorrelation.toFixed(2)
              : '—'
          }
          accent={corrAccent}
          onClick={() => setOpen('correlation')}
        />
        <Chip
          label="Capital"
          tooltip={`Общий капитал фонда: ₽${formatMillions(capitalMap.total)} в ${summaries.length} подах. Тап — стэкбар + долю каждого пода.`}
          value={`₽${formatMillions(capitalMap.total)}`}
          onClick={() => setOpen('capital')}
        />
        <Chip
          label="Conc"
          tooltip={`Тикеров в которых сидят 2+ пода: ${concentration.length}. Тап — список с весами.`}
          value={String(concentration.length)}
          accent={concentration.length >= 4 ? '#d29922' : '#8b949e'}
          onClick={() => setOpen('concentration')}
        />
        <Chip
          label="Flow"
          tooltip={`Событий перераспределения капитала: ${capitalFlow.length}. Тап — таймлайн.`}
          value={String(capitalFlow.length)}
          onClick={() => setOpen('capital-flow')}
        />
        <Chip
          label="Feed"
          tooltip={
            lastEvent
              ? `Лента событий: ${feed.length} записей. Последнее: ${lastEvent.type}. Тап — полная лента.`
              : `Лента событий пуста. Ожидаем первое событие.`
          }
          value={String(feed.length)}
          accent="#58a6ff"
          onClick={() => setOpen('event-feed')}
        />
      </nav>

      <BottomSheet
        open={open === 'capital'}
        title="Capital allocation"
        onClose={close}
      >
        <CapitalAllocationSection result={capitalMap} collapsible={false} />
      </BottomSheet>
      <BottomSheet open={open === 'pnl'} title="Daily P&L" onClose={close}>
        <PnlSection distribution={pnl} collapsible={false} />
      </BottomSheet>
      <BottomSheet open={open === 'alerts'} title="Alerts" onClose={close}>
        <AlertsSection rollup={alerts} collapsible={false} />
      </BottomSheet>
      <BottomSheet open={open === 'risk'} title="Risk states" onClose={close}>
        <RiskBreakdownSection breakdown={riskBreakdown} collapsible={false} />
      </BottomSheet>
      <BottomSheet
        open={open === 'concentration'}
        title="Concentration"
        onClose={close}
      >
        <ConcentrationSection rows={concentration} collapsible={false} />
      </BottomSheet>
      <BottomSheet
        open={open === 'correlation'}
        title="Correlation"
        onClose={close}
      >
        <CorrelationMatrixSection
          matrix={correlation}
          podNames={podNames}
          collapsible={false}
          isMobile={true}
        />
      </BottomSheet>
      <BottomSheet
        open={open === 'capital-flow'}
        title="Capital flow"
        onClose={close}
      >
        <CapitalFlowSection events={capitalFlow} collapsible={false} />
      </BottomSheet>
      <BottomSheet
        open={open === 'event-feed'}
        title="Event feed"
        rightSlot={
          <span style={{ color: '#8b949e', fontSize: 11 }}>{feed.length}</span>
        }
        onClose={close}
      >
        {/* EventFeed already renders its own scrollable region, but
            inside the sheet we want it to take the available height
            without its own borders.  Wrap in a height-limited box. */}
        <div style={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
          <EventFeed />
        </div>
      </BottomSheet>
    </>
  );
}

/**
 * One compact chip in the strip.  Designed to fit a 2-line layout on a
 * 88-ish px wide cell -- label on top, big value below.  Tap target is
 * the entire chip; min-height keeps fingers happy.
 */
function Chip({
  label,
  tooltip,
  value,
  accent,
  onClick,
}: {
  label: string;
  tooltip: string;
  value: React.ReactNode;
  accent?: string;
  onClick: () => void;
}) {
  return (
    <Tooltip content={tooltip} maxWidth={300} style={{ cursor: 'pointer' }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: 2,
          padding: '6px 8px',
          background: '#161b22',
          border: '1px solid #21262d',
          borderRadius: 8,
          minHeight: 44,
          boxSizing: 'border-box',
          // Don't let a really long value spill outside the cell --
          // truncate gracefully so the grid stays even.
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <span
          style={{
            color: '#8b949e',
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            lineHeight: 1,
          }}
        >
          {label}
        </span>
        <span
          style={{
            color: accent ?? '#e6edf3',
            fontSize: 13,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {value}
        </span>
      </button>
    </Tooltip>
  );
}
